import { t } from "@/translations"
import {
    type AddressVerificationIssue,
    type AddressVerificationIssueCode
} from "@/types/loqate/verify"

const TRANSLATION_PREFIX =
    "checkout.information_for_billing_and_shipping.address_verification"

/** Every string the address verification dialog renders, already resolved to one language. */
export interface AddressVerificationCopy {
    youEntered: string
    couldNotVerifyTitle: string
    couldNotVerifyDescription: string
    verificationIssuesLabel: string
    editAddressButton: string
    useAsEnteredButton: string
    issues: Record<AddressVerificationIssueCode, string>
}

/** What Sanity returns: the same strings, each one blank unless an editor filled it in. */
export type SanityAddressVerificationTranslations = Partial<
    Omit<AddressVerificationCopy, "issues">
> &
    Partial<Record<`issue${string}`, string>>

const resolve = (sanity: string | undefined, fallback: string) =>
    sanity && sanity.trim() !== "" ? sanity : fallback

/** Same sentence on the dialog and the field, including the typed value when we have one. */
export const formatVerificationIssueText = (label: string, value?: string): string =>
    value?.trim() ? `${label} ${value.trim()}` : label

type VerificationIssueField = NonNullable<AddressVerificationIssue["field"]>

/**
 * One message per form field the issues name. Whole-address problems land on the street
 * line. Two issues on the same field are joined rather than one silently replacing the other.
 */
export const verificationIssueFieldMessages = (
    issues: AddressVerificationIssue[],
    copy: AddressVerificationCopy
): Array<{ field: VerificationIssueField; message: string }> => {
    const byField = new Map<VerificationIssueField, string[]>()

    for (const issue of issues) {
        const field = issue.field ?? "address1"
        const text = formatVerificationIssueText(copy.issues[issue.code], issue.value)
        const existing = byField.get(field)
        if (existing) {
            existing.push(text)
        } else {
            byField.set(field, [text])
        }
    }

    return [...byField.entries()].map(([field, texts]) => ({
        field,
        message: texts.join(" ")
    }))
}

/**
 * Field by field, so an untouched document changes nothing and one override replaces
 * only that string.
 */
export const buildAddressVerificationCopy = (
    langCode: string,
    sanity?: SanityAddressVerificationTranslations | null
): AddressVerificationCopy => {
    const fallback = (key: string) => t(langCode, `${TRANSLATION_PREFIX}.${key}`)

    return {
        youEntered: resolve(sanity?.youEntered, fallback("you_entered")),
        couldNotVerifyTitle: resolve(
            sanity?.couldNotVerifyTitle,
            fallback("unverified_title")
        ),
        couldNotVerifyDescription: resolve(
            sanity?.couldNotVerifyDescription,
            fallback("unverified_description")
        ),
        verificationIssuesLabel: resolve(
            sanity?.verificationIssuesLabel,
            fallback("verification_issues")
        ),
        editAddressButton: resolve(sanity?.editAddressButton, fallback("edit_address")),
        useAsEnteredButton: resolve(
            sanity?.useAsEnteredButton,
            fallback("use_as_entered")
        ),
        issues: {
            premise_not_found: resolve(
                sanity?.issuePremiseNotFound,
                fallback("issue_premise_not_found")
            ),
            street_not_found: resolve(
                sanity?.issueStreetNotFound,
                fallback("issue_street_not_found")
            ),
            house_number_missing: resolve(
                sanity?.issueHouseNumberMissing,
                fallback("issue_house_number_missing")
            ),
            postal_code_not_found: resolve(
                sanity?.issuePostalCodeNotFound,
                fallback("issue_postal_code_not_found")
            ),
            city_not_found: resolve(
                sanity?.issueCityNotFound,
                fallback("issue_city_not_found")
            ),
            state_not_found: resolve(
                sanity?.issueStateNotFound,
                fallback("issue_state_not_found")
            ),
            po_box_not_allowed: resolve(
                sanity?.issuePoBoxNotAllowed,
                fallback("issue_po_box_not_allowed")
            ),
            no_match_found: resolve(
                sanity?.issueNoMatchFound,
                fallback("issue_no_match_found")
            ),
            unverified: resolve(sanity?.issueUnverified, fallback("issue_unverified")),
            poor_match_quality: resolve(
                sanity?.issuePoorMatchQuality,
                fallback("issue_poor_match_quality")
            ),
            significantly_corrected: resolve(
                sanity?.issueSignificantlyCorrected,
                fallback("issue_significantly_corrected")
            )
        }
    }
}
